import { useSectionVisible } from 'appContainer/SectionVisible';
import useDevice from 'hooks/DesktopDevice.hook';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { BoxOverflow } from 'styles';
import Stack from 'styles/stack/Stack';
import palette from 'styles/theme/Palette';
import Typography from 'styles/typography/Typograpy';
import { IBenefit } from 'types/Member';
import { SECTIONS } from 'types/SectionsVisibility';
import { useTranslation } from 'utils/translations';
import { getShortDate } from 'utils/utils';
import MemberValueBenefit from './MemberValueBenefit.component';

const MemberBenefits: FC = () => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: 'dbLabels',
	});
	const { isDesktop, isTabletLandScape } = useDevice();
	const { member } = useSelector((store: RootState) => store.member);

	const isVisusChangeVisible = useSectionVisible(
		SECTIONS.VISUS_CHANGE,
		member?.carrier,
	);

	const headerPlansList = isVisusChangeVisible
		? [
				{
					title: 'planBenefit',
					width: isTabletLandScape ? 194 : 256,
				},
				{
					title: 'memberBenefit',
					width: isTabletLandScape ? 150 : 255,
				},
				{
					title: 'eligible',
					width: isTabletLandScape ? 80 : 130,
				},
				{
					title: 'eligibleAsOfDate',
					width: isTabletLandScape ? 150 : 160,
				},
				{
					title: 'frequency',
					width: isTabletLandScape ? 150 : 248,
				},
				{
					title: 'visusChange',
					width: isTabletLandScape ? 194 : 399,
				},
			]
		: [
				{
					title: 'planBenefit',
					width: isTabletLandScape ? 180 : 350,
				},
				{
					title: 'memberBenefit',
					width: isTabletLandScape ? 180 : 350,
				},
				{
					title: 'eligible',
					width: isTabletLandScape ? 80 : 200,
				},
				{
					title: 'eligibleAsOfDate',
					width: isTabletLandScape ? 150 : 350,
				},
				{
					title: 'frequency',
					width: isTabletLandScape ? 190 : 350,
				},
			];

	const exam = member?.benefit.exam;
	const materials = member?.benefit.materials;

	const examAvailable = exam?.filter((e: IBenefit) => e.typology !== 'NC');
	const materialAvailable = materials?.filter(
		(e: IBenefit) => e.typology !== 'NC',
	);

	const shouldRenderHeader =
		examAvailable &&
		materialAvailable &&
		(examAvailable.length > 0 || materialAvailable.length > 0);

	const showExamsSection = examAvailable && examAvailable.length > 0;
	const showMaterialsSection = materialAvailable && materialAvailable.length > 0;

	return (
		<BoxOverflow
			variant="x"
			sx={{
				maxWidth: '100%',
				paddingBottom: '5px',
			}}
		>
			<Stack display={'inline-block'} minWidth={'100%'}>
				{shouldRenderHeader && (
					<Stack
						direction={'row'}
						height={isDesktop ? 42 : 40}
						alignItems={'center'}
						justifyItems={'start'}
						sx={{ px: '20px', py: '10px' }}
					>
						{headerPlansList.map(
							(item: { title: string; width: number | string }) => (
								<Typography
									key={item.title}
									variant="bodyLink"
									width={item.width}
									minWidth={item.width}
									sx={{ pr: '1rem' }}
								>
									{tClientLabels(`memberDetail.labels.${item.title}`)}
								</Typography>
							),
						)}
					</Stack>
				)}

				{showExamsSection && (
					<Stack
						direction={'row'}
						height={isDesktop ? 42 : 36}
						alignItems={'center'}
						sx={{
							px: '20px',
							py: '8px',
							background: palette.grey5.main,
							borderTopLeftRadius: '4px',
							borderTopRightRadius: '4px',
						}}
					>
						<Typography variant="bodyLink" width={242}>
							{tClientLabels(`memberDetail.labels.exam`)}
						</Typography>
					</Stack>
				)}
				{showExamsSection &&
					examAvailable.map((exam: IBenefit, index: number) => (
						<Stack
							key={`member_row_exam_${index}`}
							direction={'row'}
							minHeight={56}
							alignItems={'center'}
							mt={'2px'}
							sx={{
								paddingX: '20px',
								paddingY: '16px',
								background: 'white',
								borderBottomLeftRadius:
									member?.benefit.exam.length === index + 1 ? '4px' : 0,
								borderBottomRightRadius:
									member?.benefit.exam.length === index + 1 ? '4px' : 0,
							}}
						>
							<Typography variant="body" minWidth={headerPlansList[0].width} pr="1rem">
								{exam.planBenefit ? exam.planBenefit : tDbLabels(exam.serviceLabel)}
							</Typography>
							<MemberValueBenefit benefit={exam} width={headerPlansList[1].width} />
							<Typography variant="body" minWidth={headerPlansList[2].width} pr="1rem">
								{exam.eligible > 0
									? tClientLabels('commons.yes')
									: tClientLabels('commons.no')}
							</Typography>
							<Typography variant="body" minWidth={headerPlansList[3].width} pr="1rem">
								{getShortDate(exam.eligibleAsOfDate)}
							</Typography>
							<Typography variant="body" minWidth={headerPlansList[4].width} pr="1rem">
								{exam.frequencyLabel ? tDbLabels(exam.frequencyLabel) : ''}
							</Typography>
							{isVisusChangeVisible ? (
								<Typography variant="body" pr="1rem">
									{exam.visusChangeLabel ? tDbLabels(exam.visusChangeLabel) : ''}
								</Typography>
							) : (
								<></>
							)}
						</Stack>
					))}

				{showMaterialsSection && (
					<Stack
						direction={'row'}
						height={isDesktop ? 42 : 36}
						alignItems={'center'}
						mt={1.25}
						sx={{
							paddingX: '20px',
							paddingY: '8px',
							background: palette.grey5.main,
							borderTopLeftRadius: '4px',
							borderTopRightRadius: '4px',
						}}
					>
						<Typography variant="bodyLink" width={170}>
							{tClientLabels(`memberDetail.labels.materials`)}
						</Typography>
					</Stack>
				)}
				{showMaterialsSection &&
					materialAvailable.map((material: IBenefit, index: number) => (
						<Stack
							key={`member_benefit_row_${index}`}
							direction={'row'}
							minHeight={56}
							alignItems={'center'}
							mt={'2px'}
							sx={{
								paddingX: '20px',
								paddingY: '16px',
								background: 'white',
								borderBottomLeftRadius:
									member?.benefit.materials.length === index + 1 ? '4px' : 0,
								borderBottomRightRadius:
									member?.benefit.materials.length === index + 1 ? '4px' : 0,
							}}
						>
							<Typography
								variant="body"
								width={headerPlansList[0].width}
								minWidth={headerPlansList[0].width}
								pr="1rem"
							>
								{material.planBenefit
									? material.planBenefit
									: tDbLabels(material.serviceLabel)}
							</Typography>
							<MemberValueBenefit
								benefit={material}
								width={headerPlansList[1].width}
							/>
							<Typography
								variant="body"
								width={headerPlansList[2].width}
								minWidth={headerPlansList[2].width}
								pr="1rem"
							>
								{material.eligible > 0
									? tClientLabels('commons.yes')
									: tClientLabels('commons.no')}
							</Typography>
							<Typography variant="body" minWidth={headerPlansList[3].width} pr="1rem">
								{getShortDate(material.eligibleAsOfDate)}
							</Typography>
							<Typography variant="body" minWidth={headerPlansList[4].width} pr="1rem">
								{material.frequencyLabel ? tDbLabels(material.frequencyLabel) : ''}
							</Typography>
							{isVisusChangeVisible ? (
								<Typography variant="body" pr="1rem">
									{material.visusChangeLabel ? tDbLabels(material.visusChangeLabel) : ''}
								</Typography>
							) : (
								<></>
							)}
						</Stack>
					))}
			</Stack>
		</BoxOverflow>
	);
};

export default MemberBenefits;
